import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import { alpha, useTheme } from '@mui/material/styles'
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../../../../generic/hooks/useAuthorization'
import { filterOperations } from '../operationFilter'
import { BALANCED_ORDER, NATURAL_ORDER, isBalancedWorkflow, orderOperations } from '../operationOrder'
import useOperationExpansions from '../useOperationExpansions'
import { raisedColor } from '../detailSurfaces'
import { CONTROL_RADIUS } from '../detailControls'
import OperationRow from './OperationRow'
import {
  ACTIONS_COLUMN, ACTIONS_COLUMN_WIDTH, ELASTIC_OPERATION_COLUMN, MACHINE_COLUMN, PLANNED_WORKER_COLUMN,
  THEORETICAL_TIME_COLUMN, stickyColumn
} from './operationColumns'
import { isBlank } from '../detailShared'

// The order the user last chose for balanced workflows, remembered per browser. Storage can be
// missing or throw (private window, blocked site data): then the tab simply opens in natural order.
const ORDER_STORAGE_KEY = 'foDetail.operationsOrder'
const readStoredOrder = () => {
  try {
    return window.localStorage.getItem(ORDER_STORAGE_KEY) === BALANCED_ORDER ? BALANCED_ORDER : NATURAL_ORDER
  } catch (error) {
    return NATURAL_ORDER
  }
}
const storeOrder = (mode) => {
  try {
    window.localStorage.setItem(ORDER_STORAGE_KEY, mode)
  } catch (error) {
    // Not remembering the choice is all that is lost.
  }
}

// ADDENDUM 44 — una cifra de la celda de cantidades. El guion NO es un cero maquillado: es la
// ausencia dicha (RN8), y por eso tiene su propio color apagado y su propio testid, para que un
// test pueda distinguir "no llegó" de "vale cero" sin leer el color.
// §2.4 del handoff — la «eyebrow»: 11 px, 600, versalitas y `letter-spacing: .12em`. Las cabeceras
// de esta tabla ya eran rótulos apagados en versalitas; lo que adopta el lienzo es la MEDIDA, que es
// lo que hace que un rótulo de columna se lea como rótulo y no compita con el dato de debajo.
const ColumnHeader = ({ children, align = 'left' }) => (
  <Typography
    variant='caption'
    // ⚠️ S423 (23/09, 1440 px): una PALABRA más ancha que su columna no se parte sola, y se montaba en
    // la de al lado («THEORETICALPLANNED»). `overflowWrap: 'anywhere'` es el último recurso: parte la
    // palabra DENTRO de su columna. Los anchos están pensados para que en inglés y español no haga
    // falta; en alemán, euskera o letón («Planifikatutako», «Teorētiskais») sí.
    sx={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'text.secondary', textAlign: align, overflowWrap: 'anywhere' }}
  >
    {children}
  </Typography>
)

// CU-L3/CU-L4/CU-W3: the operations list (N3), its per-row dropdown (N4, without opening another
// window) and inline planning-group editing (N5) — T3, plan-jupiter.md §5.
const OperationsBlock = ({
  fabricationOrderId,
  operations = [],
  onOpenWorkflow = () => {},
  // PO request 2026-09-14: next to the legend, the workflow the jobs of this FO belong to. Since H187
  // (RN17) the caller passes the single formatter's text built from X2 `sheet.workflow.code` + `.origin`
  // (FabricationOrderDetailDialog.jsx), never `sheet.workflow.name`.
  workflowName = '',
  // RN42/E10: the same condition the listing row already gates its own "Workflow" action with
  // (`row.workflow.used`, FabricationOrder.jsx) — this is the same underlying FabricationOrder
  // object X2 serves, not a new field (no addendum needed, see plan-jupiter.md §9).
  workflowUsed = false,
  // H179 (RN5, C6): una OF sin workflow no tiene editor que abrir; el botón no se ofrece.
  offersOpenWorkflow = true,
  // The balancing of the order's workflow (X2 `sheet.workflow.balancedType`): 'TYPE', 'LINE' or ''.
  // Only a balanced workflow offers the natural / balanced order switch.
  balancedType = '',
  // The saved group lives in the container's data too: the container re-reads it so the other
  // blocks that show the group (and a later remount of this one) do not bring the old value back.
  onPlanningGroupSaved = () => {},
  // The cache of what the opened rows load, shared with Progress and kept by the container. Without
  // it (the block mounted alone) the block keeps its own.
  expansionCache
}) => {
  const ownExpansionCache = useOperationExpansions(fabricationOrderId)
  const cache = expansionCache || ownExpansionCache
  const { expansions, theoreticals } = cache
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const isUserAuthorized = useAuthorization()
  const canEditGroup = isUserAuthorized('WORKFLOWS_TAB')
  // The pinned pair of the header (the rows pin theirs in `OperationRow`).
  const stickyActionsColumn = stickyColumn(0, theme)
  const stickyGroupColumn = {
    ...stickyColumn(ACTIONS_COLUMN_WIDTH, theme),
    borderLeft: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`
  }
  // Las celdas fijadas se salen del fondo de la fila para quedarse opacas mientras el resto scrollea
  // por debajo, así que repiten el nivel de la cabecera — que desde la pasada 25 es un color opaco de
  // la escala, no un tinte superpuesto.
  const stickyHeadTint = { backgroundColor: raisedColor(theme) }

  // S357 — el desplegable se ajusta al ancho VISIBLE, no al de la tabla.
  //
  // ⚠ Defecto visto por el PO en factory-lab: al desplegar Details los rótulos de la derecha salían
  // cortados a media palabra. La causa, medida: la celda del desplegable vive dentro de la tabla, así
  // que su ancho es el de la TABLA (`max(960, contenido)`), y el contenedor que la envuelve tiene
  // `overflow-x`. Cuando la tabla es más ancha que el hueco visible, el desplegable se recorta en ese
  // borde. Demostrado alargando el contenido de la TABLA sin tocar el desplegable: la tabla pasó de
  // 960 a 966, el desborde de 7px a 13px y el desplegable de 959 a 964 — crecen juntos.
  //
  // ⚠ No se reproduce en cualquier ventana, y no es "cuanto más estrecha peor": el menú lateral se
  // colapsa por el camino, así que el hueco medía 1009 a 1440px, **953 a 1280px** y 983 a 1130px.
  // A 1440 no desborda nada. Mirarlo solo ahí habría cerrado el defecto como no reproducible.
  //
  // Se mide con ResizeObserver porque desde dentro de la tabla NO hay forma en CSS de referirse al
  // contenedor de scroll: `100%` es el ancho de la tabla, que es justo el que sobra.
  // A callback ref and not an effect with `[]`: the container only mounts while there are rows to
  // show, so after an empty search the NEW node must be observed.
  const [visibleWidth, setVisibleWidth] = useState(0)
  const observerRef = useRef(null)
  const scrollBoxRef = useCallback((node) => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = null
    // jsdom has no ResizeObserver: without it the width stays that of the table, as before.
    if (!node || typeof ResizeObserver === 'undefined') return
    const update = () => setVisibleWidth(node.clientWidth)
    update()
    observerRef.current = new ResizeObserver(update)
    observerRef.current.observe(node)
  }, [])

  const [rows, setRows] = useState(operations)
  useEffect(() => { setRows(operations) }, [operations])

  // S447 (ADDENDUM 30 / E36 B) — buscar por código o nombre, y la leyenda filtrando.
  //
  // ⚠️ `rows` sigue siendo TODAS las operaciones y no se toca: lo que se filtra es lo que se PINTA.
  // Es lo que permite que el contador diga "14 de 30" en vez de "14 de 14", y también lo que hace
  // que `usedPlanningGroups` —las opciones del desplegable de grupo— no encoja según se teclea:
  // se derivan de `rows`, y un grupo que existe en la orden sigue siendo una opción válida aunque
  // su fila esté oculta ahora mismo.
  const [search, setSearch] = useState('')

  // Natural / balanced order. The sort is applied to what the search leaves visible, never to `rows`:
  // the counter, the planning-group options and the inline edit keep working on the natural list.
  // A workflow that is not balanced always shows the natural order, whatever was remembered.
  const offersBalancedOrder = isBalancedWorkflow(balancedType)
  const [orderMode, setOrderMode] = useState(readStoredOrder)
  const shownOrder = offersBalancedOrder ? orderMode : NATURAL_ORDER
  const balancedView = shownOrder === BALANCED_ORDER
  const changeOrder = (event) => {
    const next = event.target.checked ? BALANCED_ORDER : NATURAL_ORDER
    setOrderMode(next)
    storeOrder(next)
  }
  // The box answers at once; the filter and the table follow the deferred value, so typing stays
  // responsive on a large order and a newer key interrupts the render of the older one.
  const deferredSearch = useDeferredValue(search)
  const { rows: visibleRows, total, shown } = useMemo(() => {
    const filtered = filterOperations(rows, deferredSearch)
    return { ...filtered, rows: orderOperations(filtered.rows, shownOrder) }
  }, [rows, deferredSearch, shownOrder])

  // S446 (ADDENDUM 30 / E36 C) — VARIOS desplegables abiertos a la vez, para poder comparar dos
  // jobs sin ir y volver. Era un único `expandedId`: abrir uno cerraba el anterior.
  //
  // El cambio es pequeño porque la carga ya estaba preparada: `expansions` SIEMPRE fue un mapa por
  // operación —con su estado y sus datos—, así que lo único que forzaba uno solo era este valor
  // suelto. Lo que se abrió antes sigue cargado y no se vuelve a pedir.
  //
  // ⚠️ El `sticky` del addendum 23 NO se pelea consigo mismo con varios abiertos: cada desplegable
  // se ancla DENTRO DE SU PROPIA CELDA, y las celdas están en filas distintas, así que no comparten
  // bloque contenedor. Cada uno se queda a la vista en su fila mientras la tabla se desplaza.
  const [expandedIds, setExpandedIds] = useState([])
  // `expansions` (N4) y `theoreticals` (S427-S429: lo teórico llega por su PROPIA puerta, pedida a la
  // vez que N4: si una falla, la otra se sigue enseñando) viven en la caché compartida de arriba.

  // Los grupos ya usados EN ESTA ORDEN, derivados de las filas cargadas (N3 ya trae `planningGroup`
  // en cada fila): valores distintos, sin vacíos, ordenados. Se recalculan con las filas, así que un
  // grupo recién guardado aparece como opción para las siguientes sin recargar nada.
  const usedPlanningGroups = useMemo(() => [...new Set(rows.map((row) => row.planningGroup).filter((group) => !isBlank(group)))].sort(), [rows])

  // Only one row edits its group at a time; the draft and the error live in the row's cell.
  const [editingId, setEditingId] = useState(null)

  // A failed read is not cached: opening the row again asks again. The reads of the rows that are
  // open go out here, so a reset of the shared cache asks again for them.
  useEffect(() => {
    expandedIds.forEach((jobReferenceId) => {
      cache.ensure('expansions', jobReferenceId)
      cache.ensure('theoreticals', jobReferenceId)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedIds, cache.version])

  // The row callbacks are stable (they read the latest values through refs), so a memoized row does
  // not render again because its parent did.
  const { retry } = cache
  const expandedIdsRef = useRef(expandedIds)
  expandedIdsRef.current = expandedIds
  const retryExpansion = useCallback((jobReferenceId) => {
    retry('expansions', jobReferenceId)
    retry('theoreticals', jobReferenceId)
  }, [retry])
  const toggleExpand = useCallback((jobReferenceId) => {
    if (expandedIdsRef.current.includes(jobReferenceId)) {
      setExpandedIds((prev) => prev.filter((id) => id !== jobReferenceId))
      return
    }
    setExpandedIds((prev) => [...prev, jobReferenceId])
    retryExpansion(jobReferenceId)
  }, [retryExpansion])

  const startEditGroup = useCallback((jobReferenceId) => setEditingId(jobReferenceId), [])
  const stopEditGroup = useCallback(() => setEditingId(null), [])
  // The saved group lives in the container's data too: it re-reads it so the other blocks that show
  // the group (and a later remount of this one) do not bring the old value back.
  const onPlanningGroupSavedRef = useRef(onPlanningGroupSaved)
  onPlanningGroupSavedRef.current = onPlanningGroupSaved
  const applySavedGroup = useCallback((id, result) => {
    setRows((prev) => prev.map((current) => current.jobReferenceId === id
      ? { ...current, planningGroup: result.planningGroup, planningGroupOrigin: result.planningGroupOrigin }
      : current))
    setEditingId(null)
    onPlanningGroupSavedRef.current(id, result)
  }, [])

  const workflowDisabled = !canEditGroup || workflowUsed
  const workflowDisabledReasonKey = !canEditGroup
    ? 'You do not have permission to open the workflow'
    : workflowUsed
      ? 'This workflow is already in use'
      : ''

  return (
    <Box data-testid='operations-block'>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent='space-between'
        data-testid='operations-toolbar'
        sx={{ mb: 2 }}
      >
        {/* ⚠️ AQUÍ HUBO UNA LEYENDA DE ESTADOS, Y LUEGO UN FILTRO, Y AHORA NO HAY NADA.
            La historia importa porque este párrafo ha cambiado de bando TRES veces y cada giro
            costó algo:
              · decía que los chips "sí se pulsan" siendo falso, y de ahí salió un encargo de
                trabajo para un filtro que nadie había escrito;
              · se corrigió a "son una leyenda y NO tienen onClick";
              · S447 escribió el filtro de verdad y la corrección pasó a ser falsa.
            Y el cuarto giro es éste: el PO ha visto el filtro funcionando y ha decidido QUITAR LOS
            CHIPS ENTEROS —no sustituirlos por un desplegable, que se le ofreció—. La lección no es
            cuál de las cuatro frases acertaba: es que un comentario que afirma una CAPACIDAD
            envejece sin que nada lo desmienta, porque nadie lo ejecuta.
            Queda SOLO el buscador, en una línea. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap='wrap' useFlexGap>
          {/* La clave es la que YA usa el editor de workflow para buscar operaciones
              (`ProcessTreeView`): el mismo concepto se llama igual en las dos pantallas, que es la
              misma razón por la que la casilla EXT de S340 copió su palabra del editor. */}
          <TextField
            size='small'
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t_('Search operation')}
            inputProps={{ 'data-testid': 'operations-search', 'aria-label': t_('Search operation') }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'><SearchIcon fontSize='small' sx={{ color: 'text.secondary' }} /></InputAdornment>
              ),
              // El aspa solo existe cuando hay algo que borrar: un control permanentemente inerte
              // es el defecto que S371 costó arreglar en este mismo fichero.
              endAdornment: search !== ''
                ? (
                  <InputAdornment position='end'>
                    <IconButton size='small' aria-label={t_('Clear')} data-testid='operations-search-clear' onClick={() => setSearch('')}>
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                  )
                : undefined
            }}
            sx={{ minWidth: { sm: 220 } }}
          />
          {/* ⚠️ "N de M" AL LADO DEL BUSCADOR, y SOLO con búsqueda activa. No es el contador que se
              retiró con los chips —ni su sitio, ni sus dos líneas, ni su presencia permanente—: es
              la RESPUESTA a lo que el usuario acaba de teclear.
              Existe porque quitar aquel contador dejaba una pérdida real: el buscador puede ocultar
              16 de 30 filas y nada lo diría hasta que las ocultara TODAS y saltara la frase de
              vacío. Eso es RN8 aplicado a la búsqueda —una ausencia se declara— y no dependía de
              los chips. Con el campo vacío no se pinta nada: no hay nada que responder. */}
          {search.trim() !== '' && (
            <Typography data-testid='operations-search-count' variant='body2' sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
              {t_('{{shown}} of {{total}}', { shown, total })}
            </Typography>
          )}
          {offersBalancedOrder && (
            <FormControlLabel
              sx={{ m: 0, whiteSpace: 'nowrap' }}
              control={
                <Switch
                  size='small'
                  checked={balancedView}
                  onChange={changeOrder}
                  inputProps={{ 'data-testid': 'operations-balanced-order-toggle', 'aria-label': t_('Balanced order') }}
                />
              }
              label={<Typography variant='body2'>{t_('Balanced order')}</Typography>}
            />
          )}
        </Stack>

        <Stack direction='row' spacing={2.5} alignItems='center'>
          {/* Revisión del PO (pasada 24): esto es un DATO de solo lectura, y estaba vestido de chip
              igual que los estados de al lado. Un control falso invita a pulsarlo: ahora es rótulo +
              valor, en texto, fuera de ese grupo.
              ⚠️ Este comentario decía "los cuatro filtros de estado de al lado, QUE SÍ SE PULSAN",
              se corrigió en el addendum 24 a "son una LEYENDA y no tienen `onClick`", y desde S447
              vuelve a ser cierto lo primero: los chips filtran. Se queda la corrección de FONDO, que
              no ha cambiado en ninguno de los tres giros: el dato de al lado NO es un control y por
              eso no se viste de chip. Lo demás es la prueba de que un comentario que afirma una
              capacidad hay que releerlo cada vez que se toca el fichero. */}
          {!!workflowName && (
            <Box data-testid='operations-workflow-name' sx={{ minWidth: 0 }}>
              <Typography variant='caption' sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: 'text.secondary' }}>
                {t_('Workflow')}
              </Typography>
              <Typography
                variant='body2'
                title={workflowName}
                sx={{ fontWeight: 700, maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {workflowName}
              </Typography>
            </Box>
          )}
          {offersOpenWorkflow && (
            <Button
              data-testid='open-workflow-button'
              onClick={onOpenWorkflow}
              disabled={workflowDisabled}
              variant='contained'
              startIcon={<OpenInNewRounded fontSize='small' />}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: CONTROL_RADIUS }}
            >
              {t_('Open workflow')}
            </Button>
          )}
          {offersOpenWorkflow && workflowDisabled && (
            <Typography data-testid='open-workflow-disabled-reason' variant='caption' sx={{ color: 'text.secondary' }}>
              {t_(workflowDisabledReasonKey)}
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* DOS vacíos distintos, y decirlos con la misma frase sería mentir en uno de los dos: que la
          orden no tenga operaciones es un hecho de la orden; que el filtro se las haya comido es un
          hecho de lo que el usuario acaba de teclear, y tiene arreglo —de ahí el botón—. Es la misma
          distinción de RN8 entre una ausencia y un cero, aplicada a una tabla. */}
      {total === 0 && (
        <Typography data-testid='no-operations' variant='body2' sx={{ color: 'text.secondary' }}>{t_('This order has no operations')}</Typography>
      )}

      {total > 0 && shown === 0 && (
        <Stack direction='row' spacing={2} alignItems='center' data-testid='no-operations-match'>
          {/* ⚠️ ESTA FRASE SE QUEDA aunque los chips se hayan ido, y no es un resto: siguen siendo
              DOS hechos distintos. "Esta orden no tiene operaciones" es un hecho de la orden;
              "ninguna coincide" es un hecho de lo que el usuario acaba de teclear, y tiene arreglo.
              Decir la primera cuando la orden SÍ tiene operaciones y las esconde el buscador es
              afirmar algo falso — el defecto que S447 corrigió. Lo único que cambia es el rótulo:
              deja de mencionar los filtros, y pasa a la clave que el editor de workflow ya usa. */}
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>{t_('No operations match the search')}</Typography>
          <Button size='small' variant='outlined' data-testid='operations-show-all' onClick={() => setSearch('')}>{t_('Clear')}</Button>
        </Stack>
      )}

      {shown > 0 && (
        <TableContainer
          ref={scrollBoxRef}
          sx={{
            // Lienzo: 10 px (`--mic-radius-lg`), el mismo radio que el dibujo da a las filas de la
            // lista; el borde sigue siendo el de siempre.
            borderRadius: '10px',
            border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
            // Defecto 1 (pasada 21, mockup 02-operaciones.png): macOS hides an overlay scrollbar by
            // default, so the horizontal scroll this container already offers reads as the last
            // column being cut off — a permanently visible, discreet bar (Firefox via
            // scrollbarWidth, WebKit via the pseudo-elements) makes the affordance obvious instead.
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha(theme.palette.text.primary, 0.24)} ${alpha(theme.palette.text.primary, 0.06)}`,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: alpha(theme.palette.text.primary, 0.06) },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.text.primary, 0.24),
              borderRadius: 4
            }
          }}
        >
          <Table size='small' aria-label={t_('Operations')} sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: raisedColor(theme) }}>
                {/* ⚠️⚠️ ADDENDUM 44 (E50) — CÓDIGO Y NOMBRE ERAN DOS COLUMNAS Y AHORA SON UNA CELDA,
                    que es el movimiento propio del mockup: identificador arriba en pequeño, nombre
                    debajo en grande. No es cosmética — es lo que paga las tres columnas nuevas.
                    El PO pidió AÑADIR datos sin quitar ninguna columna, y el suelo medido de esta
                    tabla ya son 1.267 px (E43), así que lo único que cabe es densificar en
                    vertical dentro de la celda. `jobOrder` se retoma aquí como "· OP nnnn" cuando
                    existe: sigue vacío en las 2.907 filas de esta base, pero el mockup lo pide y
                    el hueco no se inventa (RN8) — si no hay, no se escribe.
                    La tecnología sigue viajando bajo el nombre, como desde la pasada 22. */}
                <TableCell sx={ELASTIC_OPERATION_COLUMN}><ColumnHeader>{t_('Operation')}</ColumnHeader></TableCell>
                <TableCell sx={{ minWidth: 130 }}><ColumnHeader>{t_('Status')}</ColumnHeader></TableCell>
                {/* ⚠️⚠️ S423 (D6, plan del 23/09) — AQUÍ IBAN «OK / pend. / rech. / inc.» y «Tiempo contra
                    el teórico», y se MUDARON a la fila de Progress (S431), que es donde vive lo real.
                    Workflow enseña lo planificado: en su lugar, el TIEMPO TEÓRICO a secas. */}
                <TableCell sx={THEORETICAL_TIME_COLUMN}><ColumnHeader>{t_('Theoretical time')}</ColumnHeader></TableCell>
                {/* ⚠️ D11 (plan del 23/09): aquí estaba «Operarios que trabajaron», y se QUITA — no pasa
                    a Progress ni a ningún sitio. En su lugar, lo teórico: quién está PLANIFICADO. */}
                <TableCell sx={PLANNED_WORKER_COLUMN}><ColumnHeader>{t_('Planned worker')}</ColumnHeader></TableCell>
                {/* Defecto 1 (pasada 21): dates now render stacked/compact (never wrapping to a
                    third line), so the column no longer needs the ~230px a wrapped range used to
                    push the table to — 150px comfortably fits "MM/DD/YYYY HH:mm – HH:mm". */}
                <TableCell sx={{ minWidth: 120 }}><ColumnHeader>{t_('Planned dates')}</ColumnHeader></TableCell>
                <TableCell sx={MACHINE_COLUMN}><ColumnHeader>{t_('Device')}</ColumnHeader></TableCell>
                <TableCell sx={{ minWidth: 170, ...stickyGroupColumn, ...stickyHeadTint }}><ColumnHeader>{t_('Planning group')}</ColumnHeader></TableCell>
                <TableCell sx={{ ...ACTIONS_COLUMN, ...stickyActionsColumn, ...stickyHeadTint }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <OperationRow
                  key={row.jobReferenceId}
                  row={row}
                  fabricationOrderId={fabricationOrderId}
                  balancedView={balancedView}
                  expanded={expandedIds.includes(row.jobReferenceId)}
                  expansion={expansions[row.jobReferenceId]}
                  theoretical={theoreticals[row.jobReferenceId]}
                  expandedWidth={expandedIds.includes(row.jobReferenceId) ? visibleWidth : 0}
                  editingGroup={editingId === row.jobReferenceId}
                  canEditGroup={canEditGroup}
                  usedPlanningGroups={usedPlanningGroups}
                  onToggle={toggleExpand}
                  onRetry={retryExpansion}
                  onStartEditGroup={startEditGroup}
                  onStopEditGroup={stopEditGroup}
                  onGroupSaved={applySavedGroup}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ⚠️⚠️ RN34/S199 DICHO EN PANTALLA, que es la única parte del lienzo que aquí AÑADE algo en
          vez de repintarlo. El dibujo cierra la tabla con «… en orden de proceso — la lista es una
          secuencia, no una bandeja».
          El porqué: el resto del rediseño ordena por urgencia (§4 del handoff manda Bloqueo →
          Atención → En curso → …), y el PO decidió que AQUÍ NO se reordena — el orden lo pone Sun y
          es el del proceso. Sin decirlo, quien conozca las otras listas leerá esta como si también
          estuviera priorizada y entenderá que la primera fila es la más urgente. Lo que reclama
          atención lo marcan las píldoras, sin mover nada de sitio.
          Se pinta solo cuando hay filas: sin tabla no hay orden del que hablar. */}
      {shown > 0 && (
        <Typography data-testid='operations-process-order-note' variant='caption' sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          {t_(balancedView ? 'Operations are listed in balanced order' : 'Operations are listed in process order')}
        </Typography>
      )}
    </Box>
  )
}

export default OperationsBlock
